import { theme as defaultTheme } from "@mindlercare/ui-web";
import { IcbtResponse } from "api/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import type { Document } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Typography } from "components/typography";
import { Timestamp } from "./Timestamp";
import { type ContentfulRichContentLinks } from "@mindlercare/icbt";

interface PatientNoteResponseProps {
  response?: IcbtResponse;
  description?: {
    json: Document;
    links: ContentfulRichContentLinks;
  };
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const Answer = styled(Typography)`
  background-color: ${() => defaultTheme.COLORS.GRAY_LIGHT};
  padding: 10px;
  border-radius: 4px;
  white-space: pre-wrap;
`;

/**
 * Renders a single Contentful-placed patient note's answer inline within the
 * program page. The note's label comes from the preceding IcbtExerciseLabel
 * block, so this only renders the patient's response text (or an empty state).
 */
export const PatientNoteResponse: React.FC<PatientNoteResponseProps> = ({
  response,
  description,
}) => {
  const { t } = useTranslation();
  if (!response) {
    return (
      <Wrapper>
        {description?.json
          ? documentToReactComponents(description?.json)
          : null}
        <Answer variant="body" insensitive>
          {t("NO_NOTES")}
        </Answer>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {description?.json ? documentToReactComponents(description?.json) : null}
      <Answer variant="body">{response.response_text}</Answer>
      <Timestamp status="edited" showAccountSymbol={true} alignSelf="flex-end">
        {response.updated_at}
      </Timestamp>
    </Wrapper>
  );
};
